import { CircuitDayFormData } from '@/types/circuits/CircuitDaysFormData';
import { format } from 'date-fns';
import { LOCALES_MAP } from '@/constants/localeMap';
import {
  Dispatch,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
  useMemo,
  useState,
} from 'react';
import HorizontalLine from '@/components/ui/HorizontalLine';
import { BenefitType } from '@/types/benefits/benefitTypes';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import EstimateBenefitImagesModal from '../EstimateBenefitImagesModal';
import StyledExpandableText from '@/components/styled/StyledExpandableText';
import { useImageCalculatedHeight } from '@/hooks/useImageCalculatedHeight';
import StyledButton from '@/components/styled/StyledButton';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useConvertPriceOnlyIfDateISProvided } from '@/hooks/useCurrencyExchange';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBenefitTypeTranslatedLabel } from '@/utils/benefits/getBenefitTypeName';

interface EstimateDayStepCardTemplateProps {
  step: CircuitDayFormData['steps'][number]['transport'];
  type: BenefitType | null;
  stepDates: {
    startDate: Date;
    endDate: Date;
  } | null;
  standardName: string;
  carRentalLongDescription?: string;
  displayOptionalBenefits: boolean;
  setDisplayOptionalBenefits: Dispatch<SetStateAction<boolean>>;
  displayOptionalBenefitsPredicate?: (
    step: CircuitDayFormData['steps'][number]['transport'],
  ) => boolean;
  radioGroupElement?: ReactNode;
  generalBenefitInformation?: Record<string, string | undefined>;
  standardBenefitOptions?: ReactNode;
  imageUrl?: string;
  imagesKeys?: string[];
  titlePrice?: number;
  nbParticipants?: number;
  shouldDisplayDetailsBtn?: boolean;
  shouldDisplayReadMoreBtn?: boolean;
  currency?: string;
  creationDate?: Date;
  accessToken?: string;
  language?: SupportedLocale;
}

const EstimateDayStepCardTemplate = ({
  step,
  type,
  stepDates,
  standardName,
  carRentalLongDescription,
  displayOptionalBenefits,
  setDisplayOptionalBenefits,
  displayOptionalBenefitsPredicate = (step) => step.optionalBenefits.length > 0,
  radioGroupElement,
  generalBenefitInformation,
  standardBenefitOptions,
  imageUrl,
  imagesKeys = [],
  titlePrice,
  nbParticipants,
  shouldDisplayDetailsBtn = true,
  shouldDisplayReadMoreBtn = true,
  currency = 'EUR',
  creationDate,
  accessToken,
  language,
  children,
}: PropsWithChildren<EstimateDayStepCardTemplateProps>) => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const convertPrice = useConvertPriceOnlyIfDateISProvided(creationDate);

  const [isImagesModalOpen, setIsImagesModalOpen] = useState(false);

  const { imgRef, calculatedHeight } = useImageCalculatedHeight({
    defaultHeight: 150,
  });

  const shouldDisplayedName = useMemo(
    () => step.standardDisplays.includes('name'),
    [step.standardDisplays],
  );

  const shortDescription = useMemo(() => {
    const shouldDisplayShortDescription =
      step.standardDisplays.includes('shortDescription');
    if (!shouldDisplayShortDescription) return '';

    const translations = language
      ? step.standardBenefit?.translations?.[language]
      : undefined;

    return (
      translations?.activity?.shortDescription ??
      translations?.flight?.shortDescription ??
      translations?.VehicleWithChauffeur?.shortDescription ??
      translations?.hotel?.shortDescriptionBody ??
      step.standardBenefit?.activity?.shortDescription ??
      step.standardBenefit?.flight?.shortDescription ??
      step.standardBenefit?.VehicleWithChauffeur?.shortDescription ??
      step.standardBenefit?.hotel?.shortDescriptionBody ??
      ''
    );
  }, [
    step.standardDisplays,
    step.standardBenefit?.translations,
    step.standardBenefit?.activity?.shortDescription,
    step.standardBenefit?.flight?.shortDescription,
    step.standardBenefit?.VehicleWithChauffeur?.shortDescription,
    step.standardBenefit?.hotel?.shortDescriptionBody,
    language,
  ]);

  const longDescription = useMemo(() => {
    const shouldDisplayLongDescription =
      step.standardDisplays.includes('longDescription');
    if (!shouldDisplayLongDescription) return '';

    const translations = language
      ? step.standardBenefit?.translations?.[language]
      : undefined;

    return (
      translations?.activity?.longDescription ??
      translations?.flight?.longDescription ??
      translations?.VehicleWithChauffeur?.longDescription ??
      translations?.hotel?.longDescriptionBody ??
      step.standardBenefit?.activity?.longDescription ??
      step.standardBenefit?.flight?.longDescription ??
      step.standardBenefit?.VehicleWithChauffeur?.longDescription ??
      step.standardBenefit?.hotel?.longDescriptionBody ??
      carRentalLongDescription ??
      ''
    );
  }, [
    step.standardDisplays,
    step.standardBenefit?.translations,
    step.standardBenefit?.activity?.longDescription,
    step.standardBenefit?.flight?.longDescription,
    step.standardBenefit?.VehicleWithChauffeur?.longDescription,
    step.standardBenefit?.hotel?.longDescriptionBody,
    carRentalLongDescription,
    language,
  ]);

  const vehicleWithChauffeurNbCars = useMemo(() => {
    if (!nbParticipants) return null;
    const capacity =
      step.standardBenefit?.VehicleWithChauffeur?.maxCapacity ?? null;
    if (!capacity) return null;
    return Math.ceil(nbParticipants / capacity);
  }, [nbParticipants, step.standardBenefit?.VehicleWithChauffeur?.maxCapacity]);

  return (
    (step.standardDisplays.length > 0 || step.optionalDisplays.length > 0) && (
      <div className="flex w-full flex-col gap-4">
        <HorizontalLine />
        {step.standardDisplays.length > 0 && (
          <div className="flex flex-col gap-4 w-full">
            <div className="flex w-full flex-col gap-4 md:flex-row-reverse md:items-start">
              {imageUrl && (
                <div className="shrink-0 self-center md:self-start">
                  <img
                    src={imageUrl}
                    ref={imgRef}
                    onClick={() => {
                      if (
                        (imagesKeys && imagesKeys.length > 0) ||
                        longDescription
                      )
                        setIsImagesModalOpen(true);
                    }}
                    className="h-auto max-h-56 w-72 min-w-72 rounded-lg object-cover object-center hover:cursor-pointer lg:w-80 lg:min-w-80"
                  />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="px-3 py-1.5 rounded-full bg-brand-600 text-sm font-normal text-white w-fit">
                  {type ? getBenefitTypeTranslatedLabel(t, type) : ''}
                </div>
                {stepDates !== null && (
                  <div className="flex flex-col gap-1 mt-1">
                    <span className="text-sm font-normal text-gray-800">
                      {t('estimates.details.dayStep.from')}{' '}
                      {format(stepDates.startDate, 'PPPP', {
                        locale: LOCALES_MAP[currentLanguage],
                      })}{' '}
                      {t('estimates.details.dayStep.to')}{' '}
                      {format(stepDates.endDate, 'PPPP', {
                        locale: LOCALES_MAP[currentLanguage],
                      })}
                    </span>
                    <span className="text-sm font-normal text-gray-800">
                      {step.nbDays}{' '}
                      {type === BenefitType.HOTEL
                        ? t('estimates.details.dayStep.nights')
                        : t('estimates.details.dayStep.days')}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {radioGroupElement}
                  <h2 className="text-sm font-bold leading-6 text-gray-800">
                    {shouldDisplayedName
                      ? vehicleWithChauffeurNbCars
                        ? `${vehicleWithChauffeurNbCars} × ${standardName}`
                        : standardName
                      : ''}
                  </h2>
                  {titlePrice != undefined && (
                    <span className="text-sm leading-6 font-semibold text-brand-700">
                      {titlePrice > 0 ? '+ ' : ''}
                      {`${formatPrice(convertPrice(titlePrice, 'EUR', currency))} ${formatCurrency(currency)}`}
                    </span>
                  )}
                </div>
                {shouldDisplayReadMoreBtn ? (
                  <StyledExpandableText
                    text={shortDescription}
                    maxHeight={calculatedHeight - 50}
                  />
                ) : (
                  <span className="text-sm 2xl:text-base font-normal text-gray-800 whitespace-pre-wrap">
                    {shortDescription}
                  </span>
                )}
                {(longDescription.length > 0 || imagesKeys.length > 0) &&
                  shouldDisplayDetailsBtn && (
                    <StyledButton
                      title={t('estimates.details.dayStep.showDetail')}
                      onClick={() => {
                        setIsImagesModalOpen(true);
                      }}
                      variant="link"
                      className="p-0 text-brand-700"
                    />
                  )}
                {Object.entries(generalBenefitInformation ?? {})
                  .filter(([, v]) => v)
                  .map(([infoKey, infoValue], infoIndex) =>
                    ['Compris', 'Non compris'].includes(infoKey) && infoValue ? (
                      <ul
                        className="text-sm font-normal text-gray-800 list-disc"
                        key={`estimate-step-information-${infoKey}-${infoIndex}`}
                      >
                        {infoKey} :
                        {infoValue
                          .split('\n')
                          .map((bulletPoint, indexBulletPoint) => (
                            <li
                              key={`estimate-step-information-${infoKey}-${indexBulletPoint}`}
                              className="ml-4"
                            >
                              {bulletPoint}
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <p
                        className="text-sm font-normal text-gray-800"
                        key={`estimate-step-information-${infoKey}-${infoIndex}`}
                      >
                        {infoKey} : {infoValue}
                      </p>
                    ),
                  )}
              </div>
            </div>
            {standardBenefitOptions}
          </div>
        )}
        {displayOptionalBenefitsPredicate(step) && (
          <div className="flex w-full flex-col gap-4">
            <div
              className={clsx(
                'flex justify-between items-center w-full px-4 py-2 border border-brand-600 rounded-lg hover:cursor-pointer',
                {
                  'bg-white': displayOptionalBenefits,
                  'bg-brand-100': !displayOptionalBenefits,
                },
              )}
              onClick={() => {
                setDisplayOptionalBenefits((prev) => !prev);
              }}
            >
              <span className="text-sm font-normal text-brand-800">
                {t('estimates.details.dayStep.optionalBenefits')}
              </span>
              {displayOptionalBenefits ? (
                <ChevronUpIcon className="stroke-brand-800 size-6" />
              ) : (
                <ChevronDownIcon className="stroke-brand-800 size-6" />
              )}
            </div>
            {displayOptionalBenefits && children}
          </div>
        )}
        {isImagesModalOpen && (
          <EstimateBenefitImagesModal
            closeModal={() => {
              setIsImagesModalOpen(false);
            }}
            title={standardName}
            description={longDescription}
            imageKeys={imagesKeys}
            accessToken={accessToken}
          />
        )}
      </div>
    )
  );
};

export default EstimateDayStepCardTemplate;
